import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { FiPlus, FiEdit2, FiTrash2, FiLayers, FiCheck, FiX } from 'react-icons/fi';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCatName, setNewCatName] = useState('');
  const [editingCat, setEditingCat] = useState(null);
  const [editCatName, setEditCatName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { showToast } = useToast();

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/categories');
      if (res.success) {
        setCategories(res.data);
      }
    } catch (err) {
      showToast(err.message || 'Failed to fetch categories', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCatName || newCatName.trim() === '') {
      showToast('Please enter category name', 'error');
      return;
    }

    try {
      const res = await api.post('/categories', { name: newCatName });
      if (res.success) {
        showToast('Category created successfully!', 'success');
        setNewCatName('');
        fetchCategories();
      }
    } catch (err) {
      showToast(err.message || 'Failed to create category', 'error');
    }
  };

  const handleUpdate = async (id) => {
    if (!editCatName || editCatName.trim() === '') return;
    try {
      const res = await api.put(`/categories/${id}`, { name: editCatName });
      if (res.success) {
        showToast('Category renamed successfully!', 'success');
        setEditingCat(null);
        fetchCategories();
      }
    } catch (err) {
      showToast(err.message || 'Failed to rename category', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await api.delete(`/categories/${deleteTarget._id}`);
      if (res.success) {
        showToast('Category deleted successfully', 'success');
        setDeleteTarget(null);
        fetchCategories();
      }
    } catch (err) {
      showToast(err.message || 'Failed to delete category', 'error');
    }
  };

  return (
    <div style={{ maxWidth: '800px' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.75rem', fontWeight: 800 }}>
          Product Categories
        </h1>
        <p style={{ color: '#94A3B8', fontSize: '0.95rem' }}>
          Group your products into categories (e.g. Sarees, Shirts, Accessories) for easy browsing.
        </p>
      </div>

      {/* CREATE CATEGORY FORM */}
      <div className="glass-card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.15rem', fontWeight: 700, marginBottom: '1rem' }}>
          Add New Category
        </h3>
        <form onSubmit={handleCreate} style={{ display: 'flex', gap: '1rem' }}>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Sarees, Shirts, Accessories"
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
          />
          <button type="submit" className="btn btn-primary">
            <FiPlus /> Add Category
          </button>
        </form>
      </div>

      {/* CATEGORIES LIST */}
      {loading ? (
        <LoadingSpinner text="Loading categories..." />
      ) : categories.length === 0 ? (
        <EmptyState
          title="No Categories Created"
          description="Create categories to help customers filter your products on your public page."
          icon={FiLayers}
        />
      ) : (
        <div className="glass-card">
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.15rem', fontWeight: 700, marginBottom: '1.25rem' }}>
            Existing Categories ({categories.length})
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {categories.map((cat) => (
              <div
                key={cat._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.85rem 1.25rem',
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                {editingCat === cat._id ? (
                  <div style={{ display: 'flex', gap: '0.5rem', flex: 1, marginRight: '1rem' }}>
                    <input
                      type="text"
                      className="form-control"
                      value={editCatName}
                      onChange={(e) => setEditCatName(e.target.value)}
                    />
                    <button onClick={() => handleUpdate(cat._id)} className="btn btn-primary btn-sm">
                      <FiCheck />
                    </button>
                    <button onClick={() => setEditingCat(null)} className="btn btn-secondary btn-sm">
                      <FiX />
                    </button>
                  </div>
                ) : (
                  <span style={{ fontSize: '1rem', fontWeight: '600', color: '#fff' }}>
                    {cat.name}
                  </span>
                )}

                {editingCat !== cat._id && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => {
                        setEditingCat(cat._id);
                        setEditCatName(cat.name);
                      }}
                      className="btn btn-secondary btn-sm"
                      title="Rename"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(cat)}
                      className="btn btn-danger btn-sm"
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CONFIRM DELETE DIALOG */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Category"
        message={`Are you sure you want to delete category "${deleteTarget?.name}"? Linked products will become uncategorized.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default CategoriesPage;
